import { Spinner } from "@blueprintjs/core";
import { observer } from "mobx-react";
import { LocalLoader } from "modules/common/components/loader/Loader";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import React from "react";
import InfiniteScroll from "react-infinite-scroller";
import { DataRowSnapshotType, OrderLogsStoreType, OrderLogSnapshotType } from "../models/order-logs";
import styles from "./LogList.module.scss";

/**
 * Одно изменение: пометка действия, вкладка, поле и значения «было → стало».
 * Вложенные строки — поля элемента коллекции, поэтому рисуются тем же компонентом.
 */
const Change = ({ row, nested }: { row: DataRowSnapshotType; nested?: boolean }) => {
  const kind = row.isNew ? "added" : row.isRemove ? "removed" : row.isUpdate ? "updated" : null;
  const children = (row as any).rows as DataRowSnapshotType[] | null;

  return (
    <div className={nested ? styles.nested : styles.change}>
      <div className={styles.line}>
        {kind && (
          <span className={`${styles.badge} ${styles[kind]}`}>
            {kind === "added" ? "Создано" : kind === "removed" ? "Удалено" : "Изменено"}
          </span>
        )}

        {row.tab && <span className={styles.tab}>{row.tab}</span>}

        <span className={styles.field}>{row.fieldName}</span>

        <span className={styles.values}>
          {row.prevValue && <span className={styles.prev}>{row.prevValue}</span>}
          {row.prevValue && row.value && (
            <GeneralIcon type="general-big-arrow-right" size={10} className={styles.arrow} />
          )}
          {row.value && <span className={styles.value}>{row.value}</span>}
        </span>
      </div>

      {children && children.length > 0 && (
        <div className={styles.children}>
          {children.map((child, i) => (
            <Change key={i} row={child} nested />
          ))}
        </div>
      )}
    </div>
  );
};

/** Запись истории: кто и когда менял, и что именно */
const Entry = ({ entry }: { entry: OrderLogSnapshotType }) => (
  <div className={styles.entry}>
    <div className={styles.head}>
      <GeneralIcon type="general-clock" size={12} className={styles.clock} />
      <span className={styles.date}>{entry.dateText}</span>
      <span className={styles.time}>{entry.time}</span>
      {entry.user && <span className={styles.user}>{entry.user}</span>}
      <span className={styles.count}>
        {entry.rows.length} {plural(entry.rows.length)}
      </span>
    </div>

    <div className={styles.changes}>
      {entry.rows.map((row, i) => (
        <Change key={i} row={row} />
      ))}
    </div>
  </div>
);

const plural = (n: number) => {
  const last = n % 10;
  const two = n % 100;

  if (two >= 11 && two <= 14) return "изменений";
  if (last === 1) return "изменение";
  if (last >= 2 && last <= 4) return "изменения";
  return "изменений";
};

class LogItems extends React.Component<LogListProps> {
  scroller = React.createRef<HTMLDivElement>();

  async componentDidMount() {
    await this.props.store.load(this.props.orderId);
  }

  getScrollParent = () => this.scroller.current;

  render() {
    const { store } = this.props;

    if (store.loading) {
      return (
        <div className={styles.wrapper}>
          <LocalLoader active={true} />
        </div>
      );
    }

    return (
      <div className={styles.wrapper}>
        <div className={styles.scroller} ref={this.scroller}>
          {store.data.length === 0 && <div className={styles.empty}>История изменений пуста</div>}

          {store.data.length > 0 && (
            <InfiniteScroll
              pageStart={1}
              loadMore={() => store.loadNext(this.props.orderId)}
              hasMore={store.hasMore}
              useWindow={false}
              initialLoad={false}
              threshold={200}
              getScrollParent={this.getScrollParent}
            >
              {store.data.map((entry) => (
                <Entry key={entry.id} entry={entry as any} />
              ))}
            </InfiniteScroll>
          )}

          {store.loadingNext && (
            <div className={styles.more}>
              <Spinner intent="primary" size={16} />
              <span>Загружаем ещё…</span>
            </div>
          )}

          {!store.loadingNext && !store.hasMore && store.data.length > 0 && (
            <div className={styles.more}>Это начало истории</div>
          )}
        </div>
      </div>
    );
  }
}

export const LogList = observer(LogItems);

interface LogListProps {
  store: OrderLogsStoreType;
  orderId: string;
}
